import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import BackButton from '../../components/common/BackButton';

const ManageNurses = () => {
    const { t } = useTranslation();
    const [nurses, setNurses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [editingNurse, setEditingNurse] = useState(null);
    const [formData, setFormData] = useState({
        nameAr: '',
        nameEn: '',
        specialtyAr: '',
        specialtyEn: '',
        jobTitle: 'Nurse',
        image: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [imagePreview, setImagePreview] = useState('');

    useEffect(() => {
        fetchNurses();
    }, []);

    const fetchNurses = async () => {
        try {
            const res = await axios.get('https://hasanhaji.com/api/team');
            // Filter only nurses
            const nursesOnly = res.data.data.teamMembers
                .filter(member => member.jobTitle === 'Nurse')
                .sort((a, b) => a.order - b.order);
            setNurses(nursesOnly);
            setLoading(false);
        } catch (err) {
            setError(t('admin.errorLoadingTeam'));
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const submitData = new FormData();
            submitData.append('nameAr', formData.nameAr);
            submitData.append('nameEn', formData.nameEn);
            submitData.append('specialtyAr', formData.specialtyAr);
            submitData.append('specialtyEn', formData.specialtyEn);
            submitData.append('jobTitle', 'Nurse'); // Always Nurse

            const imageFile = document.getElementById('imageUpload').files[0];
            if (imageFile) {
                submitData.append('image', imageFile);
            }

            const config = {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            };

            if (editingNurse) {
                await axios.patch(`https://hasanhaji.com/api/team/${editingNurse.id}`, submitData, config);
            } else {
                await axios.post('https://hasanhaji.com/api/team', submitData, config);
            }

            fetchNurses();
            resetForm();
            setIsSubmitting(false);
        } catch (err) {
            console.error(err);
            setError(t('admin.errorSavingMember'));
            setIsSubmitting(false);
        }
    };

    const handleEdit = (nurse) => {
        setEditingNurse(nurse);
        setFormData({
            nameAr: nurse.nameAr || nurse.name || '',
            nameEn: nurse.nameEn || nurse.name || '',
            specialtyAr: nurse.specialtyAr || nurse.specialty || '',
            specialtyEn: nurse.specialtyEn || nurse.specialty || '',
            jobTitle: 'Nurse',
            image: nurse.image || '',
        });
        setImagePreview(nurse.image || '');
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = async (id) => {
        if (window.confirm(t('admin.confirmDeleteMember'))) {
            try {
                await axios.delete(`https://hasanhaji.com/api/team/${id}`);
                fetchNurses();
            } catch (err) {
                setError(t('admin.errorDeletingMember'));
            }
        }
    };

    const handleMove = async (index, direction) => {
        if (loading || isSubmitting) return;

        const currentNurse = nurses[index];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;

        if (targetIndex < 0 || targetIndex >= nurses.length) return;

        const targetNurse = nurses[targetIndex];

        setIsSubmitting(true);
        try {
            const currentOrder = currentNurse.order;
            const targetOrder = targetNurse.order;

            await Promise.all([
                axios.patch(`https://hasanhaji.com/api/team/${currentNurse.id}`, { order: targetOrder }),
                axios.patch(`https://hasanhaji.com/api/team/${targetNurse.id}`, { order: currentOrder })
            ]);

            await fetchNurses();
        } catch (err) {
            console.error(err);
            setError(t('admin.errorReordering'));
            fetchNurses();
        } finally {
            setIsSubmitting(false);
        }
    };

    const resetForm = () => {
        setEditingNurse(null);
        setFormData({
            nameAr: '',
            nameEn: '',
            specialtyAr: '',
            specialtyEn: '',
            jobTitle: 'Nurse',
            image: '',
        });
        setImagePreview('');
        const imageUpload = document.getElementById('imageUpload');
        if (imageUpload) imageUpload.value = '';
    };

    return (
        <div className="container-fluid py-5 bg-light">
            <div className="container">
                <div className="d-flex align-items-center mb-5">
                    <BackButton to="/admin" />

                    <h2 className="fw-bold text-info">
                        <i className="fas fa-user-nurse me-2"></i> {t('admin.manageNurses', 'إدارة الممرضين')}
                    </h2>
                </div>

                {error && (
                    <div className="alert alert-danger shadow-sm border-0" role="alert">
                        <i className="fas fa-exclamation-circle me-2"></i> {error}
                    </div>
                )}

                <div className="row g-4">
                    {/* Form Section */}
                    <div className="col-lg-4">
                        <div className="card border-0 shadow-sm sticky-top" style={{ top: '20px', zIndex: 1 }}>
                            <div className="card-header bg-white border-bottom-0 pt-4 px-4">
                                <h5 className="card-title fw-bold mb-0 text-dark">
                                    {editingNurse ? t('admin.editNurse', 'تعديل ممرض/ة') : t('admin.addNurse', 'إضافة ممرض/ة')}
                                </h5>
                            </div>
                            <div className="card-body p-4">
                                <form onSubmit={handleSubmit}>
                                    {/* Image Upload */}
                                    <div className="mb-4 text-center">
                                        <div className="position-relative d-inline-block">
                                            <div
                                                className="rounded-circle bg-light d-flex align-items-center justify-content-center border"
                                                style={{ width: '120px', height: '120px', overflow: 'hidden', margin: '0 auto' }}
                                            >
                                                {imagePreview ? (
                                                    <img src={imagePreview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                ) : (
                                                    <i className="fas fa-user-nurse fa-3x text-secondary"></i>
                                                )}
                                            </div>
                                            <label
                                                htmlFor="imageUpload"
                                                className="btn btn-sm btn-info position-absolute bottom-0 end-0 rounded-circle shadow-sm"
                                                style={{ width: '32px', height: '32px', padding: '0', lineHeight: '32px' }}
                                            >
                                                <i className="fas fa-camera"></i>
                                            </label>
                                        </div>
                                        <input
                                            type="file"
                                            className="d-none"
                                            id="imageUpload"
                                            accept="image/*"
                                            onChange={handleImageUpload}
                                        />
                                    </div>

                                    <div className="row g-3">
                                        <div className="col-6">
                                            <label className="form-label text-muted small fw-bold">{t('admin.nameAr')}</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="nameAr"
                                                value={formData.nameAr}
                                                onChange={handleChange}
                                                required
                                                placeholder={t('admin.nurseNamePlaceholder', 'فاطمة أحمد')}
                                            />
                                        </div>
                                        <div className="col-6">
                                            <label className="form-label text-muted small fw-bold">{t('admin.nameEn')}</label>
                                            <input
                                                type="text"
                                                className="form-control text-start"
                                                name="nameEn"
                                                value={formData.nameEn}
                                                onChange={handleChange}
                                                required
                                                placeholder={t('admin.nurseNameEnPlaceholder', 'Fatima Ahmed')}
                                                dir="ltr"
                                            />
                                        </div>

                                        <div className="col-6">
                                            <label className="form-label text-muted small fw-bold">{t('admin.specialtyAr')}</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="specialtyAr"
                                                value={formData.specialtyAr}
                                                onChange={handleChange}
                                                required
                                                placeholder={t('admin.nurseSpecialtyPlaceholder', 'تمريض عام')}
                                            />
                                        </div>
                                        <div className="col-6">
                                            <label className="form-label text-muted small fw-bold">{t('admin.specialtyEn')}</label>
                                            <input
                                                type="text"
                                                className="form-control text-start"
                                                name="specialtyEn"
                                                value={formData.specialtyEn}
                                                onChange={handleChange}
                                                required
                                                placeholder={t('admin.nurseSpecialtyEnPlaceholder', 'General Nursing')}
                                                dir="ltr"
                                            />
                                        </div>
                                    </div>

                                    <div className="d-grid gap-2 mt-4">
                                        <button
                                            type="submit"
                                            className="btn btn-info py-2 fw-bold text-white"
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? <span className="spinner-border spinner-border-sm me-2"></span> : <i className="fas fa-save me-2"></i>}
                                            {editingNurse ? t('admin.saveChanges') : t('admin.addNurseBtn', 'إضافة ممرض/ة')}
                                        </button>
                                        {editingNurse && (
                                            <button
                                                type="button"
                                                className="btn btn-light text-danger py-2"
                                                onClick={resetForm}
                                            >
                                                {t('admin.cancel')}
                                            </button>
                                        )}
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>

                    {/* Nurses List */}
                    <div className="col-lg-8">
                        <div className="card border-0 shadow-sm bg-transparent">
                            <div className="card-header bg-transparent border-0 p-0 mb-3">
                                <h5 className="card-title fw-bold text-dark">{t('admin.nursesList', 'قائمة الممرضين')}</h5>
                            </div>

                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-info" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : nurses.length === 0 ? (
                                <div className="alert alert-info border-0 shadow-sm" role="alert">
                                    <i className="fas fa-info-circle me-2"></i> {t('admin.noNurses', 'لا يوجد ممرضين حالياً')}
                                </div>
                            ) : (
                                <div className="row g-3">
                                    {nurses.map((nurse, index) => (
                                        <div className="col-md-6" key={nurse.id}>
                                            <div className="card border-0 shadow-sm h-100 overflow-hidden member-card">
                                                <div className="card-body p-3">
                                                    <div className="d-flex align-items-center">
                                                        <div className="flex-shrink-0">
                                                            <img
                                                                src={nurse.image || '/images/default-avatar.png'}
                                                                alt={nurse.name}
                                                                className="rounded-circle border"
                                                                style={{ width: '70px', height: '70px', objectFit: 'cover' }}
                                                                onError={(e) => { e.target.src = 'https://via.placeholder.com/70?text=N' }}
                                                            />
                                                        </div>
                                                        <div className="flex-grow-1 ms-3 me-3">
                                                            <div className="d-flex justify-content-between align-items-start">
                                                                <h6 className="fw-bold mb-1">{nurse.nameAr || nurse.name}</h6>
                                                                <span className="badge bg-info rounded-pill">
                                                                    {t('admin.nurse')}
                                                                </span>
                                                            </div>
                                                            <p className="text-muted small mb-1">{nurse.nameEn}</p>
                                                            <div className="d-flex align-items-center text-secondary small">
                                                                <i className="fas fa-stethoscope me-1 text-info opacity-50"></i>
                                                                <span>{nurse.specialtyAr || nurse.specialty}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="card-footer bg-light border-0 p-2 d-flex justify-content-between align-items-center">
                                                    <div className="btn-group">
                                                        <button
                                                            className="btn btn-sm btn-outline-secondary border-0"
                                                            onClick={() => handleMove(index, 'up')}
                                                            disabled={index === 0 || isSubmitting}
                                                            title={t('admin.moveUp')}
                                                        >
                                                            <i className="fas fa-arrow-up"></i>
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-outline-secondary border-0"
                                                            onClick={() => handleMove(index, 'down')}
                                                            disabled={index === nurses.length - 1 || isSubmitting}
                                                            title={t('admin.moveDown')}
                                                        >
                                                            <i className="fas fa-arrow-down"></i>
                                                        </button>
                                                    </div>
                                                    <div className="d-flex gap-2">
                                                        <button
                                                            className="btn btn-sm btn-outline-info border-0"
                                                            onClick={() => handleEdit(nurse)}
                                                            title={t('admin.edit')}
                                                        >
                                                            <i className="fas fa-edit"></i>
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-outline-danger border-0"
                                                            onClick={() => handleDelete(nurse.id)}
                                                            title={t('admin.delete')}
                                                        >
                                                            <i className="fas fa-trash-alt"></i>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <style jsx>{`
        .member-card {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .member-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 .5rem 1rem rgba(0,0,0,.15)!important;
        }
      `}</style>
        </div>
    );
};

export default ManageNurses;
